class ProductCalculator extends HTMLElement {
  constructor() {
    super();
    this.sqftInput = this.querySelector("[data-calculator-input]");
    this.boxesInput = this.querySelector("[data-calculator-input-boxes]");
    this.overageSelect = this.querySelector("[data-calculator-overage]");
    this.toggleBtn = this.querySelector("[data-calculator-toggle]");
    this.body = this.querySelector("[data-calculator-body]");
    this.breakdownToggle = this.querySelector("[data-breakdown-toggle]");
    this.breakdownContent = this.querySelector("[data-breakdown-content]");
    this.breakdownOverage = this.querySelector("[data-breakdown-overage]");
    this.breakdownUnits = this.querySelector("[data-breakdown-units]");
    this.breakdownCoverage = this.querySelector("[data-breakdown-coverage]");

    this.sqftPerBox = parseFloat(this.dataset.sqftPerBox) || 1;
    this.isMosaic = this.dataset.isMosaic === "true";
    this.quantityInputId = this.dataset.quantityInputId;
    this.isCalculating = false;
    this.lastChangedInput = null;
    this.init();
  }

  init() {
    if (!this.sqftInput || !this.boxesInput) return;

    this.breakdownToggle?.addEventListener("click", () => {
      const isExpanded = this.breakdownContent.style.display !== "none";
      this.breakdownContent.style.display = isExpanded ? "none" : "flex";
      this.breakdownToggle.classList.toggle("is-active", !isExpanded);
    });

    this.sqftInput.addEventListener("input", () => {
      if (!this.isCalculating) {
        this.lastChangedInput = "sqft";
        this.calculateFromSqft();
      }
    });

    this.boxesInput.addEventListener("input", () => {
      if (!this.isCalculating) {
        this.lastChangedInput = "boxes";
        this.calculateFromBoxes();
      }
    });

    this.overageSelect?.addEventListener("change", () => {
      this.recalculate();
    });
  }

  getOverageMultiplier() {
    const pct = parseInt(this.overageSelect?.value || "0", 10);
    return 1 + pct / 100;
  }

  getOverageLabel() {
    const pct = parseInt(this.overageSelect?.value || "0", 10);
    return pct === 0 ? this.overageSelect.options[0].text : "+" + pct + "%";
  }

  calculateFromSqft() {
    const sqft = parseFloat(this.sqftInput.value) || 0;

    if (sqft <= 0) {
      this.isCalculating = true;
      this.boxesInput.value = "";
      this.updateQuantityInput(0);
      this.updateBreakdown(0, 0);
      this.isCalculating = false;
      return;
    }

    const multiplier = this.getOverageMultiplier();
    const adjustedSqft = sqft * multiplier;
    const boxes = Math.ceil(adjustedSqft / this.sqftPerBox);

    this.isCalculating = true;
    this.boxesInput.value = boxes;
    this.isCalculating = false;

    this.updateQuantityInput(boxes);
    this.updateBreakdown(sqft, boxes);
  }

  calculateFromBoxes() {
    const boxes = parseInt(this.boxesInput.value, 10) || 0;

    if (boxes <= 0) {
      this.isCalculating = true;
      this.sqftInput.value = "";
      this.updateQuantityInput(0);
      this.updateBreakdown(0, 0);
      this.isCalculating = false;
      return;
    }

    const totalSqft = boxes * this.sqftPerBox;
    const sqftRounded = Math.round(totalSqft * 100) / 100;

    this.isCalculating = true;
    this.sqftInput.value = sqftRounded;
    this.isCalculating = false;

    this.updateQuantityInput(boxes);
    this.updateBreakdown(sqftRounded, boxes);
  }

  recalculate() {
    if (this.lastChangedInput === "boxes") {
      const boxes = parseInt(this.boxesInput.value, 10) || 0;
      if (boxes > 0) {
        const baseSqft = parseFloat(this.sqftInput.value) || 0;
        if (baseSqft > 0) {
          this.calculateFromSqft();
        } else {
          this.updateBreakdown(0, boxes);
        }
        return;
      }
    }
    this.calculateFromSqft();
  }

  updateBreakdown(baseSqft, boxes) {
    if (!this.breakdownOverage) return;

    this.breakdownOverage.textContent = this.getOverageLabel();

    const sqftPerUnit = Math.round(this.sqftPerBox * 1000) / 1000;
    const unitWord = this.isMosaic ? "sheets" : "boxes";
    const unitWordSingle = this.isMosaic ? "sheet" : "box";
    const eachLabel = boxes === 1
      ? boxes + " " + unitWordSingle + " (" + sqftPerUnit + " sq.ft each)"
      : boxes + " " + unitWord + " (" + sqftPerUnit + " sq.ft each)";

    this.breakdownUnits.textContent = boxes > 0 ? eachLabel : "\u2014";

    const totalCoverage = Math.round(boxes * this.sqftPerBox * 100) / 100;
    this.breakdownCoverage.textContent = totalCoverage > 0 ? totalCoverage + " sq.ft" : "\u2014";
  }

  updateQuantityInput(quantity) {
    if (!this.quantityInputId) return;

    const quantityInput = document.getElementById(this.quantityInputId);
    if (quantityInput) {
      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
}

customElements.define("mushcore-product-calculator", ProductCalculator);
